// 소비 아이템·보석 데이터와 순수 계산(D2c, 정본: docs/CONTENT-90D-PRD.md 4.5·4.6). three·DOM import 금지.
// 물약(HP·MP 소/중/대, 퀵슬롯 2칸·공용 쿨다운·연속 사용 감쇠) · 비약(30분, 접속 시간 기준, 계열별 1개) · 주문서(귀환서·부활 깃털) · 보석(홍옥·청옥·황옥 × 5등급).
// 가격은 economy.ts SHOP_PRICE·SHOP_TIER_PRICE_MULT(D0a 시뮬이 이미 소비로 넣은 값) 그대로 쓴다 — tools/sim/economy.ts가 같은 함수로 지출을 센다.
// 설계 기준(디아블로·로스트아크식): 보석은 색이 효과 종류, 등급이 크기. 같은 등급 3개 → 위 등급 1개 확정 합성(확률 없음, 가챠 없음).
// 보석 효과 합은 색마다 GEM_CAP으로 자른다(버프 합산 캡과 같은 결) — 99,999 피해 상한 불변식(헬 전엔 닿지 않는다)을 지키는 장치다.
import { SHOP_PRICE, SHOP_TIER_PRICE_MULT } from './economy';
import { BUFF_CAP, POTION_RULE } from './items';
import type { Difficulty, RegionNo } from './types';

// ── 물약(PRD 4.6) ─────────────────────────────────────
export type PotionKind = 'hp' | 'mp';
export interface PotionDef {
  readonly id: 'hp_s' | 'hp_m' | 'hp_l' | 'mp_s' | 'mp_m' | 'mp_l';
  readonly kind: PotionKind;
  /** 0 소 · 1 중 · 2 대 */
  readonly size: 0 | 1 | 2;
  readonly name: string;
  /** 최대치 대비 회복 비율(감쇠 전) */
  readonly heal: number;
  /** 상점 가격(골드, 크기가 곧 단계라 상점 배율을 곱하지 않는다 — SHOP_PRICE.potion) */
  readonly price: number;
  /** 상점에서 파는 마을(소 ①, 중 ③, 대 ⑤ — 시뮬 potionPrice의 마을 구분과 같다) */
  readonly town: number;
}
/** 물약 6종. 회복 비율 문구는 items.ts CONSUMABLES effect와 같다(content.test) */
export const POTIONS: readonly PotionDef[] = [
  { id: 'hp_s', kind: 'hp', size: 0, name: 'HP 물약(소)', heal: 0.15, price: SHOP_PRICE.potion.s, town: 1 },
  { id: 'hp_m', kind: 'hp', size: 1, name: 'HP 물약(중)', heal: 0.3, price: SHOP_PRICE.potion.m, town: 3 },
  { id: 'hp_l', kind: 'hp', size: 2, name: 'HP 물약(대)', heal: 0.5, price: SHOP_PRICE.potion.l, town: 5 },
  { id: 'mp_s', kind: 'mp', size: 0, name: 'MP 물약(소)', heal: 0.2, price: SHOP_PRICE.potion.s, town: 1 },
  { id: 'mp_m', kind: 'mp', size: 1, name: 'MP 물약(중)', heal: 0.4, price: SHOP_PRICE.potion.m, town: 3 },
  { id: 'mp_l', kind: 'mp', size: 2, name: 'MP 물약(대)', heal: 0.7, price: SHOP_PRICE.potion.l, town: 5 },
];
/**
 * 퀵슬롯(PRD 4.6 "퀵슬롯 2칸"): 0번 = HP, 1번 = MP. 누르면 가진 것 중 가장 큰 물약을 쓴다(큰 것이 없으면 작은 것).
 * 공용 쿨다운 6초(두 칸이 함께 쓴다), 연속 사용마다 회복량 −20%(마지막 사용 10초 뒤 원래대로), 아래 하한까지.
 */
export const QUICK_SLOTS: readonly PotionKind[] = ['hp', 'mp'];
/** 연속 사용 감쇠 하한(회복 배율). 5번째 연속 사용부터 20%로 고정(0이 되어 버튼이 무의미해지지 않게) */
export const POTION_DECAY_FLOOR = 0.2;
export const POTION_COOLDOWN_TICKS = Math.round(POTION_RULE.cooldownSec * 60);
export const POTION_DECAY_RESET_TICKS = Math.round(POTION_RULE.decayResetSec * 60);
/** 연속 사용 n번째(0부터)의 회복 배율 */
export function potionDecay(streak: number): number {
  return Math.max(POTION_DECAY_FLOOR, 1 - POTION_RULE.decayPerUse * Math.max(0, streak));
}
/** 자동 물약 문턱(HP 비율, 0 = 끔). 설정 창에서 고른다. 문턱 아래로 내려가고 쿨다운이 끝났으면 HP 칸을 쓴다 */
export const AUTO_POTION_OPTIONS: readonly number[] = [0, 0.3, 0.5];
/**
 * 보유 상한(한 품목). PRD 4.6 "개수 제한 없음"은 판 안에서 쓰는 수를 막지 않는다는 뜻이고(감쇠가 남용을 막는다),
 * 이 값은 저장·표시 넘침 방어다(구매가 이 값을 넘기면 limit). 999개 = 대 물약 기준 약 18만 골드.
 */
export const STACK_CAP = 999;

// ── 비약(PRD 4.6) ─────────────────────────────────────
export type ElixirFam = 'str' | 'wall' | 'luck' | 'wis';
export interface ElixirDef {
  readonly id: 'elixir_str' | 'elixir_wall' | 'elixir_luck' | 'elixir_wis';
  readonly fam: ElixirFam;
  readonly name: string;
  readonly short: string;
  /** 장비 합계 칸(GearTotals)에 더하는 값. 받는 피해 감소·드롭률·EXP는 버프 합산 캡(BUFF_CAP) 안 */
  readonly stat: 'atkPct' | 'damageTaken' | 'dropRate' | 'xp';
  readonly value: number;
  readonly durationSec: number;
}
export const ELIXIRS: readonly ElixirDef[] = [
  { id: 'elixir_str', fam: 'str', name: '힘의 비약', short: '힘', stat: 'atkPct', value: 0.1, durationSec: 1800 },
  { id: 'elixir_wall', fam: 'wall', name: '철벽의 비약', short: '철벽', stat: 'damageTaken', value: 0.08, durationSec: 1800 },
  { id: 'elixir_luck', fam: 'luck', name: '행운의 비약', short: '행운', stat: 'dropRate', value: 0.2, durationSec: 1800 },
  { id: 'elixir_wis', fam: 'wis', name: '지혜의 비약', short: '지혜', stat: 'xp', value: 0.2, durationSec: 1800 },
];
/**
 * 쌓기 규칙: 계열별 1개(효과는 겹치지 않는다). 같은 계열을 다시 마시면 남은 시간에 30분을 더하되 남은 시간은 최대 60분(두 병)까지.
 * 다른 계열은 함께 켜진다("한 번에 사용" = 가진 계열을 모두 한 병씩). 시간은 접속(플레이) 시간으로만 줄어든다(오프라인 정지).
 */
export const ELIXIR_MAX_SEC = 3600;
/** 연금술사 제작이 열리는 마을(PRD 2.3 연금술사 ④) */
export const ALCHEMIST_TOWN = 4;
export function elixirIndex(fam: ElixirFam): number { return fam === 'str' ? 0 : fam === 'wall' ? 1 : fam === 'luck' ? 2 : 3; }
/** 비약 제작비(마을 상점 단계 배율, 시뮬과 같다) */
export function elixirPrice(town: number): number { return Math.round(SHOP_PRICE.elixir * shopMult(town)); }
/** 마시는 순간(at, 플레이 초) 기준 새 끝 시각: 남은 시간 + 지속, 남은 시간 상한 ELIXIR_MAX_SEC */
export function elixirEnd(curEnd: number, at: number, durationSec: number): number {
  const from = Math.max(curEnd, at);
  return Math.min(from + durationSec, at + ELIXIR_MAX_SEC);
}

// ── 주문서(PRD 4.6) ───────────────────────────────────
export interface ScrollDef { readonly id: 'scroll_return' | 'feather_revive'; readonly name: string; readonly desc: string; readonly base: number }
/**
 * 귀환서: 던전에서 바로 마을로 돌아가며 바닥에 남은 드롭을 모두 거둔다(일시정지 창 '마을로'는 바닥 드롭을 버린다).
 * 부활 깃털: 던전에서 쓰러지면 판마다 1회 자동으로 HP 50%로 일어난다(잠깐 무적). 소탕권은 일일 무료 지급과 정산 흐름이 필요해 후속(D2c 범위 밖).
 */
export const SCROLLS: readonly ScrollDef[] = [
  { id: 'scroll_return', name: '귀환 주문서', desc: '바닥 드롭을 모두 거두고 마을로', base: SHOP_PRICE.scrollReturn },
  { id: 'feather_revive', name: '부활 깃털', desc: '쓰러지면 판마다 1회 HP 50%로 부활', base: SHOP_PRICE.featherRevive },
];
/** 부활 깃털: 일어날 때 HP 비율과 무적 틱(1.5초) */
export const REVIVE = { hp: 0.5, invulnTicks: 90 } as const;

/** 상점 단계 배율(마을 1..6) */
export function shopMult(town: number): number {
  const t = Math.max(1, Math.min(SHOP_TIER_PRICE_MULT.length, Math.floor(town)));
  return SHOP_TIER_PRICE_MULT[t - 1];
}
/** 상점 판매 품목(물약·주문서)의 지금 가격과 판매 여부. 물약은 크기 고정 가격, 주문서는 상점 배율 */
export function shopPrice(id: string, town: number): number | null {
  const p = POTIONS.find((x) => x.id === id);
  if (p) return town >= p.town ? p.price : null;
  const s = SCROLLS.find((x) => x.id === id);
  if (s) return Math.round(s.base * shopMult(town));
  return null;
}

// ── 보석·소켓(PRD 4.5) ───────────────────────────────
export type GemColor = 'ruby' | 'sapphire' | 'topaz';
export interface GemColorDef {
  readonly id: GemColor;
  readonly name: string;
  readonly role: string;
  /** 장비 합계 칸 */
  readonly stat: 'atkPct' | 'hpPct' | 'mpRegen';
  /** 1~5등급 값(소수 퍼센트) */
  readonly values: readonly [number, number, number, number, number];
  readonly color: string;
}
/**
 * 홍옥 공격력 %, 청옥 최대 HP %, 황옥 MP 재생 %. 값은 D2c 피해 시뮬(tools/sim/combat.ts gemAtk)로 정했다:
 * 색별 합 상한(GEM_CAP)이 있어 헬 전 밴드의 최강 장비 + 보석 상한도 99,999 아래(하드 전설 +15 84% → 보석 상한 포함 96%)다.
 */
export const GEM_COLORS: readonly GemColorDef[] = [
  { id: 'ruby', name: '홍옥', role: '공격', stat: 'atkPct', values: [0.003, 0.006, 0.012, 0.022, 0.04], color: '#ff5a6a' },
  { id: 'sapphire', name: '청옥', role: '방어', stat: 'hpPct', values: [0.008, 0.015, 0.025, 0.04, 0.06], color: '#5aa8ff' },
  { id: 'topaz', name: '황옥', role: '유틸', stat: 'mpRegen', values: [0.02, 0.04, 0.06, 0.09, 0.12], color: '#ffd04a' },
];
export const GEM_TIERS = 5;
export const GEM_TIER_NAME: readonly string[] = ['깨진', '흐린', '맑은', '빛나는', '완벽한'];
/** 색별 효과 합 상한(소켓을 다 채워도 이 값까지). 버프 합산 캡과 같은 결 */
export const GEM_CAP = { atkPct: 0.18, hpPct: 0.3, mpRegen: 0.5 } as const;
/** 보석 코드 = 색 번호 × 5 + (등급 − 1). 0..14. 빈 소켓은 −1 */
export const GEM_CODES = GEM_COLORS.length * GEM_TIERS;
export function gemCode(color: number, tier: number): number { return color * GEM_TIERS + (tier - 1); }
export function gemColorOf(code: number): number { return Math.floor(code / GEM_TIERS); }
export function gemTierOf(code: number): number { return (code % GEM_TIERS) + 1; }
export function isGemCode(code: unknown): code is number { return typeof code === 'number' && Number.isInteger(code) && code >= 0 && code < GEM_CODES; }
export function gemName(code: number): string { return GEM_TIER_NAME[gemTierOf(code) - 1] + ' ' + GEM_COLORS[gemColorOf(code)].name; }
export function gemValue(code: number): number { return GEM_COLORS[gemColorOf(code)].values[gemTierOf(code) - 1]; }
/** 보석 효과 문구(예: "공격력 +2%") */
export function gemEffectText(code: number): string {
  const c = GEM_COLORS[gemColorOf(code)];
  const label = c.stat === 'atkPct' ? '공격력' : c.stat === 'hpPct' ? '최대 HP' : 'MP 재생';
  return label + ' +' + (Math.round(gemValue(code) * 1000) / 10) + '%';
}
/**
 * 합성: 같은 보석 3개 → 한 등급 위 1개(확정). 수수료 = 만들 등급의 고정 값(등급이 곧 진행 단계라 상점 배율을 곱하지 않는다).
 * D2c 경제 시뮬: 상점 배율(×22.7)을 곱하면 지역6·하드에서 합성 수수료가 주 수입의 17~23%가 되어 8~9주 순수입이 적자였다 → 등급 고정 값으로
 * 주 수입의 약 1~4%(지역3 1등급 → 헬 5등급). 대안 "수수료 없음"은 보석 소각처가 사라져 고르지 않았다
 */
export const GEM_COMBINE = { need: 3, gold: [0, 1500, 5000, 16000, 50000] as readonly number[] } as const;
export function gemCombineFee(code: number, _town = 1): number {
  const to = gemTierOf(code) + 1;
  return to > GEM_TIERS ? 0 : GEM_COMBINE.gold[to - 1];
}
/** 탈착 수수료(보석은 돌려받는다): SHOP_PRICE.unsocket × 상점 배율(시뮬 unsocketsPerWeek와 같은 값) */
export function unsocketFee(town: number): number { return Math.round(SHOP_PRICE.unsocket * shopMult(town)); }
/** 보석상(소켓·합성·탈착)이 열리는 마을(PRD 2.3 보석상 ③). 보석상 NPC 전까지 대장간 보석 탭이 같은 일을 한다 */
export const JEWELER_TOWN = 3;

/**
 * 보석 드롭(PRD 4.4 "원석·보석 하드 이후" + D2c 결정: 보석상이 열리는 지역3부터 노말에서도 낮은 등급이 나온다 — 7절 페이싱 3주차 "보석").
 * 엘리트 처치마다 eliteChance, 중간보스 mid개·지역보스 final개 확정. 색은 무작위(같은 확률). 등급 = 지역 기본 + 난이도 가산(하드 +1, 헬 +2), 5 상한.
 */
export const GEM_DROP = {
  fromRegion: 3 as RegionNo,
  eliteChance: 0.03,
  boss: { mid: 1, final: 1 },
  /** 지역별 노말 기본 등급(1..6). 지역1·2는 드롭 없음(0) */
  regionTier: [0, 0, 1, 1, 2, 2] as readonly number[],
  diffAdd: { normal: 0, hard: 1, hell: 2 } as Readonly<Record<Difficulty, number>>,
} as const;
export function gemDropTier(region: RegionNo, diff: Difficulty): number {
  const base = GEM_DROP.regionTier[region - 1];
  return base <= 0 ? 0 : Math.min(GEM_TIERS, base + GEM_DROP.diffAdd[diff]);
}

/** 비약 합산(버프 캡) 확인용: 캡이 있는 칸 */
export const ELIXIR_CAPPED = { damageTaken: BUFF_CAP.damageTaken, dropRate: BUFF_CAP.dropRate, xp: BUFF_CAP.xp } as const;
